/**
 * ============================================================================
 * TrendSparkline — Phase 6b frontend (Quality Metrics Dashboard v2)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: tiny inline-SVG sparkline used inside <KpiCard>. 12 points, 2 px
 *          stroke, mint→cyan→blue gradient (per drafts/42 dual-brand palette).
 *
 * Why hand-rolled (no library):
 *   - Sparklines are 12 dots. Adding a charting dep would dwarf the surface
 *     area we render — and we don't want a transitive d3 in the bundle.
 *   - A11Y: each instance carries an SR-readable summary describing the
 *     trend direction and the value range, so the chart isn't a "mystery
 *     image" to screen readers.
 *
 * RTL: the gradient itself reads left→right; under [dir='rtl'] the bottom
 *  axis still reads "older → newer" because the parent flips orientation.
 *  We don't flip the path here — that would invert the temporal meaning.
 * ============================================================================ */

import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { MetricTrendPoint } from './useDashboardQuery';
import styles from './QualityMetricsDashboard.module.css';

export interface TrendSparklineProps {
  points: MetricTrendPoint[];
  /** Optional aria-label override; defaults to a synthesized summary. */
  ariaLabel?: string;
}

const W = 120;
const H = 36;
const PAD = 2;

export function TrendSparkline({ points, ariaLabel }: TrendSparklineProps) {
  const { t } = useTranslation();
  const gradientId = useId();

  const { d, dots, min, max, direction } = useMemo(() => {
    if (points.length === 0) {
      return {
        d: '',
        dots: [] as Array<{ cx: number; cy: number; v: number }>,
        min: 0,
        max: 0,
        direction: 'flat' as 'up' | 'down' | 'flat',
      };
    }
    const values = points.map((p) => p.value);
    const lo = Math.min(...values);
    const hi = Math.max(...values);
    // Guard zero-range so a constant series doesn't divide by zero.
    const range = hi - lo === 0 ? 1 : hi - lo;
    const step = (W - PAD * 2) / Math.max(points.length - 1, 1);
    const coords = values.map((v, i) => {
      const x = PAD + i * step;
      const y = H - PAD - ((v - lo) / range) * (H - PAD * 2);
      return { x, y, v };
    });
    const path =
      coords
        .map((c, i) => (i === 0 ? `M${c.x.toFixed(2)},${c.y.toFixed(2)}` : `L${c.x.toFixed(2)},${c.y.toFixed(2)}`))
        .join(' ');
    const first = values[0]!;
    const last = values[values.length - 1]!;
    const dir: 'up' | 'down' | 'flat' =
      last > first ? 'up' : last < first ? 'down' : 'flat';
    return {
      d: path,
      dots: coords.map((c) => ({ cx: c.x, cy: c.y, v: c.v })),
      min: lo,
      max: hi,
      direction: dir,
    };
  }, [points]);

  const summary =
    ariaLabel ??
    t('qualityMetrics.sparkline.summary', {
      defaultValue:
        'Trend over {{count}} points: {{direction}} (range {{min}}–{{max}}).',
      count: points.length,
      direction: t(`qualityMetrics.sparkline.direction.${direction}`, {
        defaultValue: direction,
      }),
      min,
      max,
    });

  if (points.length === 0) {
    return (
      <div
        className={styles.sparklineEmpty}
        role="img"
        aria-label={t('qualityMetrics.sparkline.empty', {
          defaultValue: 'No trend data yet',
        })}
      >
        —
      </div>
    );
  }

  return (
    <svg
      className={styles.sparkline}
      viewBox={`0 0 ${W} ${H}`}
      width={W}
      height={H}
      role="img"
      aria-label={summary}
      focusable="false"
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          {/* QWise editorial palette: mint → cyan → blue. */}
          <stop offset="0%" stopColor="#1DD1A1" />
          <stop offset="50%" stopColor="#22D3EE" />
          <stop offset="100%" stopColor="#2563EB" />
        </linearGradient>
      </defs>
      <path d={d} fill="none" stroke={`url(#${gradientId})`} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      {dots.map((dot, i) => (
        <circle key={i} cx={dot.cx} cy={dot.cy} r={1.5} fill={`url(#${gradientId})`}>
          <title>{`${dot.v}`}</title>
        </circle>
      ))}
    </svg>
  );
}
